import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { CookieConsentService } from '@solidgroup-agency/ui-shared-components';
import { BackToHomeComponent } from '../../../back-to-home/back-to-home.component';

@Component({
  selector: 'awo-cookies-page',
  standalone: true,
  imports: [RouterModule, MatButtonModule, BackToHomeComponent],
  template: `
    <div class="cookie-page-wrapper">
      <header class="cookie-page-header">
        <h1 class="cookie-page-title" i18n="@@cookiesPageTitle">Cookie-Richtlinie</h1>
      </header>

      <div class="cookie-page-content">
      <p i18n="@@cookiesIntro">
        Diese Website verwendet Cookies. Technisch notwendige Cookies werden
        automatisch gesetzt. Für Analyse-Cookies benötigen wir Ihre
        Einwilligung.
      </p>

      <button
        mat-flat-button
        (click)="showCookieSettings()"
        i18n="@@cookiesResetSettings"
      >
        Cookie-Einstellungen ändern
      </button>

      <h2 i18n="@@cookiesTypesTitle">Verwendete Cookie-Arten</h2>

      <div class="cookie-page-type">
        <h3 i18n="@@cookiesNecessaryTitle">Technisch notwendige Cookies</h3>
        <p i18n="@@cookiesNecessaryDesc">
          Diese Cookies sind für das ordnungsgemäße Funktionieren der Website
          erforderlich. Sie ermöglichen grundlegende Funktionen wie die
          Navigation zwischen Seiten und den Zugriff auf sichere Bereiche der
          Website. Ohne diese Cookies können bestimmte Dienste nicht
          bereitgestellt werden.
        </p>
      </div>

      <div class="cookie-page-type">
        <h3 i18n="@@cookiesAnalyticsTitle">Analyse- und Performance-Cookies (optional)</h3>
        <p i18n="@@cookiesAnalyticsDesc">
          Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website
          nutzen. Sie sammeln anonymisierte Informationen über Seitenaufrufe,
          Verweildauer und Navigationswege. Diese Daten verwenden wir zur
          Verbesserung unserer Website.
        </p>
        <p i18n="@@cookiesAnalyticsProvider">
          Google Analytics: Analyse-Tool zur Messung der Website-Nutzung
          (IP-Adresse anonymisiert).
        </p>
        <p i18n="@@cookiesPerformanceProvider">
          Firebase Performance Monitoring: Misst technische Leistungsdaten wie
          Seitenladezeiten und Antwortzeiten unserer Server, um Engpässe zu
          erkennen und die Website zu beschleunigen. Es werden keine Inhalte Ihrer
          Eingaben übertragen. Daten werden an Google (Firebase) übermittelt.
        </p>
      </div>

      <div class="cookie-page-type">
        <h3 i18n="@@cookiesMapsTitle">Google Maps</h3>
        <p i18n="@@cookiesMapsDesc">
          Diese Website nutzt Google Maps zur Anzeige interaktiver Karten und
          für die Adresssuche. Dabei werden Cookies von Google gesetzt und Daten
          an Google übertragen, um die Kartenfunktionen bereitzustellen.
        </p>
        <p i18n="@@cookiesMapsProvider">
          Google Maps API: Interaktive Karten und Adress-Autocomplete für
          Schulstandorte und Formulare.
        </p>
      </div>

      <h2 i18n="@@cookiesControlTitle">Cookie-Verwaltung</h2>
      <p i18n="@@cookiesControlDesc">
        Sie können Ihre Cookie-Einstellungen jederzeit über den oben stehenden
        Button ändern. Technisch notwendige Cookies können nicht deaktiviert
        werden, da sie für das Funktionieren der Website erforderlich sind.
      </p>

      <p i18n="@@cookiesBrowserSettings">
        Zusätzlich können Sie Cookies direkt in Ihren Browser-Einstellungen
        verwalten. Beachten Sie jedoch, dass das Deaktivieren von Cookies die
        Funktionalität der Website beeinträchtigen kann.
      </p>

      <p>
        <span i18n="@@cookiesMoreInfo"
          >Weitere Informationen über die Verarbeitung Ihrer Daten finden Sie in
          unserer</span
        >&nbsp;
        <a routerLink="/legal/privacy-policy"
          ><span i18n="@@cookiesPrivacyPolicy">Datenschutzerklärung</span></a
        >.
      </p>
      </div>

      <awo-care-back-to-home />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .cookie-page-wrapper {
        max-width: 800px;
        margin: 2rem auto;
        padding: 2rem;
        background: var(--mat-app-surface, white);
        border-radius: var(--border-radius, 8px);
        box-shadow: var(--mat-app-elevation-shadow-level-1, 0 2px 8px rgba(0, 0, 0, 0.1));
        color: #000;
      }

      .cookie-page-header {
        margin-bottom: 2rem;
      }

      .cookie-page-title {
        color: #000;
        font-size: 2rem;
        margin: 0 0 1.5rem 0;
        font-weight: 600;
      }

      .cookie-page-content {
        color: #000;
      }

      .cookie-page-content h2 {
        color: #000;
        font-size: 1.4rem;
        margin: 2rem 0 1rem 0;
        font-weight: 500;
      }

      .cookie-page-content h3 {
        color: #000;
        font-size: 1.2rem;
        margin: 1.5rem 0 0.75rem 0;
        font-weight: 500;
      }

      .cookie-page-content p {
        line-height: 1.7;
        color: #000;
        margin: 0.75rem 0;
      }

      .cookie-page-content button {
        margin: 1rem 0;
      }

      .cookie-page-type {
        margin: 1.5rem 0;
      }

      a {
        color: #1976d2;
        text-decoration: none;
        transition: color 0.2s ease;
      }

      a:hover {
        color: #1565c0;
        text-decoration: underline;
      }

      @media (max-width: 768px) {
        .cookie-page-wrapper {
          margin: 1rem auto;
          padding: 1.5rem 1rem;
          border-radius: 0;
          box-shadow: none;
        }

        .cookie-page-title {
          font-size: 1.6rem;
        }

        .cookie-page-content h2 {
          font-size: 1.2rem;
        }

        .cookie-page-content h3 {
          font-size: 1.1rem;
        }
      }
    `,
  ],
})
export class CookiesPageComponent {
  private cookieConsentService = inject(CookieConsentService);

  showCookieSettings(): void {
    // Revoke cookies to clear storage, then show banner
    this.cookieConsentService.showBanner.set(true);
  }
}
